import { NavigationMenu as NavigationMenuPrimitive } from "@base-ui/react/navigation-menu";
import { cva } from "class-variance-authority";
import { ChevronDownIcon } from "lucide-react";
import * as React from "react";

import { cn } from "@/lib/utils/cn";

const NavigationMenuRoot = ({
  className,
  ...props
}: React.ComponentProps<typeof NavigationMenuPrimitive.Root>) => (
  <NavigationMenuPrimitive.Root
    data-slot="navigation-menu"
    className={cn("relative flex max-w-max flex-1 items-center justify-center", className)}
    {...props}
  />
);

const NavigationMenuList = ({
  className,
  ...props
}: React.ComponentProps<typeof NavigationMenuPrimitive.List>) => (
  <NavigationMenuPrimitive.List
    data-slot="navigation-menu-list"
    className={cn("flex flex-1 list-none items-center justify-center gap-1", className)}
    {...props}
  />
);

const NavigationMenuItem = ({
  className,
  ...props
}: React.ComponentProps<typeof NavigationMenuPrimitive.Item>) => (
  <NavigationMenuPrimitive.Item
    data-slot="navigation-menu-item"
    className={cn("relative", className)}
    {...props}
  />
);

const navigationMenuTriggerStyle = cva(
  "focus-ring group inline-flex h-9 w-max items-center justify-center rounded-md bg-background px-4 py-2 text-sm font-medium transition-[color,box-shadow] outline-none hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground disabled:pointer-events-none disabled:opacity-50 data-[popup-open]:bg-accent/50 data-[popup-open]:text-accent-foreground",
);

const NavigationMenuTrigger = ({
  className,
  children,
  hideIcon = false,
  ...props
}: React.ComponentProps<typeof NavigationMenuPrimitive.Trigger> & { hideIcon?: boolean; }) => (
  <NavigationMenuPrimitive.Trigger
    data-slot="navigation-menu-trigger"
    className={cn(navigationMenuTriggerStyle(), className)}
    {...props}
  >
    {children}
    {!hideIcon && (
      <NavigationMenuPrimitive.Icon className="ml-1 transition-transform duration-300 data-[popup-open]:rotate-180">
        <ChevronDownIcon className="size-3" aria-hidden="true" />
      </NavigationMenuPrimitive.Icon>
    )}
  </NavigationMenuPrimitive.Trigger>
);

const NavigationMenuContent = ({
  className,
  ...props
}: React.ComponentProps<typeof NavigationMenuPrimitive.Content>) => (
  <NavigationMenuPrimitive.Content
    data-slot="navigation-menu-content"
    className={cn(
      "h-full w-full p-2 transition-[opacity,transform] data-[ending-style]:opacity-0 data-[starting-style]:opacity-0",
      className,
    )}
    {...props}
  />
);

const NavigationMenuLink = ({
  className,
  ...props
}: React.ComponentProps<typeof NavigationMenuPrimitive.Link>) => (
  <NavigationMenuPrimitive.Link
    data-slot="navigation-menu-link"
    className={cn(
      "focus-ring flex flex-col gap-1 rounded-sm p-2 text-sm outline-none transition-colors hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground data-[active]:bg-accent/50 data-[active]:text-accent-foreground [&_svg:not([class*='size-'])]:size-4 [&_svg:not([class*='text-'])]:text-muted-foreground",
      className,
    )}
    {...props}
  />
);

const NavigationMenuPortal = (
  props: React.ComponentProps<typeof NavigationMenuPrimitive.Portal>,
) => <NavigationMenuPrimitive.Portal {...props} />;

const NavigationMenuPositioner = ({
  className,
  sideOffset = 8,
  ...props
}: React.ComponentProps<typeof NavigationMenuPrimitive.Positioner>) => (
  <NavigationMenuPrimitive.Positioner
    data-slot="navigation-menu-positioner"
    sideOffset={sideOffset}
    className={cn("z-50", className)}
    {...props}
  />
);

const NavigationMenuPopup = ({
  className,
  ...props
}: React.ComponentProps<typeof NavigationMenuPrimitive.Popup>) => (
  <NavigationMenuPrimitive.Popup
    data-slot="navigation-menu-popup"
    className={cn(
      "relative origin-[var(--transform-origin)] overflow-hidden rounded-md border bg-popover text-popover-foreground shadow-md transition-[opacity,transform,width,height] duration-200 data-[ending-style]:opacity-0 data-[starting-style]:opacity-0",
      className,
    )}
    {...props}
  />
);

const NavigationMenuViewport = ({
  className,
  ...props
}: React.ComponentProps<typeof NavigationMenuPrimitive.Viewport>) => (
  <NavigationMenuPrimitive.Viewport
    data-slot="navigation-menu-viewport"
    className={cn("relative", className)}
    {...props}
  />
);

export const NavigationMenu = Object.assign(NavigationMenuRoot, {
  List: NavigationMenuList,
  Item: NavigationMenuItem,
  Trigger: NavigationMenuTrigger,
  Content: NavigationMenuContent,
  Link: NavigationMenuLink,
  Portal: NavigationMenuPortal,
  Positioner: NavigationMenuPositioner,
  Popup: NavigationMenuPopup,
  Viewport: NavigationMenuViewport,
});
